/*
 * cf-clear-when-unbuildable.js — Click Fencing — 2026-09-07
 *
 * Pete: "There are several large bugs emerging as i use it and the errors that stop people
 * progressing through to cart have to stop."
 *
 * THE DEFECT
 * ----------
 * When the solver finds no layout for a section, nothing clears what is already on screen.
 * The previous fence stays drawn and the previous price stays in the breakdown, so the
 * customer is looking at a confident drawing and a confident number for a section the
 * calculator will not actually let them buy.
 *
 * Measured on the live theme, 2026-09-07:
 *   PerfMesh, gate+rake, section set to 800mm  -> still displayed $4,280.16
 *   Flat top, gate+rake, section set to 800mm  -> still drew a ~12,000mm fence
 *      (1200 rake + 970 gate + 2400 + 2400 + 2400 + 2280), left over from the previous
 *      configuration, inside an 800mm section
 *
 * That is the worst kind of wrong: it looks right. cf-snap-to-buildable.js now moves most
 * of these sections to a length that DOES build, so this is the backstop for the cases it
 * cannot rescue — mid-edit states, a section restored from sessionStorage, or a length the
 * solver cannot solve at any nearby value.
 *
 * WHAT IT DOES
 * ------------
 * After each recalculation, if a section has a length entered but the solver produced no
 * layout, it removes THAT SECTION'S lines from the quote breakdown, re-renders the quote,
 * and replaces the drawing with a plain explanation instead of a stale fence.
 *
 * Scoping is deliberate:
 *   - Only lines tagged with that section number are removed. Whole-job lines (delivery,
 *     installation) and every other section are untouched.
 *   - "No layout" means no total AND no perfomesh block. An element-only run — rake only,
 *     gate only, gate+rake only — carries perfomesh and a real total and is left alone;
 *     those are real fences and they draw correctly.
 *   - It never edits the solver, a price, a variant id or a cart line. Display only.
 */
(function () {
  "use strict";

  if (!/(blade-fence-calculator|flat-top-fence-calculator|perfmesh-fence|glass-fencing)/.test(location.pathname)) return;
  if (window.__cfClearUnbuildable) return;
  window.__cfClearUnbuildable = true;

  var TRIES = 0, MAX_TRIES = 200;

  function inst() { return window.glassCalculator || window.glassFencingCalculator || null; }
  function num(v) { var n = parseInt(v, 10); return isFinite(n) ? n : 0; }

  // No layout at all. An in-band element-only run is NOT this — it has a perfomesh block
  // and a real total, and it draws a proper fence.
  function unbuildable(fc, n) {
    try {
      var s = fc.state && fc.state.sections && fc.state.sections[n];
      if (!s || !num(s.length)) return false;
      var c = fc.state.optimizedConfigs && fc.state.optimizedConfigs[n];
      if (!c) return true;
      if (c.perfomesh) return false;
      if (num(c.total) > 0) return false;
      // Glass reports no `total` field at all; a real glass layout still carries panels.
      if (c.total === undefined) {
        var pw = c.panelWidths;
        if (Object.prototype.toString.call(pw) === "[object Array]" && pw.length > 0) return false;
        if (num(c.numPanels) > 0) return false;
        return false;   // glass shape we do not recognise — never guess, leave it alone
      }
      return true;
    } catch (e) { return false; }
  }

  function stripSectionLines(fc, n) {
    try {
      var bd = fc.state.quoteBreakdown;
      if (Object.prototype.toString.call(bd) !== "[object Array]") return false;
      var before = bd.length;
      for (var i = bd.length - 1; i >= 0; i--) {
        var row = bd[i];
        if (row && row.section != null && String(row.section) === String(n)) bd.splice(i, 1);
      }
      return bd.length !== before;
    } catch (e) { return false; }
  }

  function blankPreview(n) {
    try {
      var el = document.getElementById("section-preview");
      if (!el) return;
      // Only blank it while THIS section is the one on screen.
      var fc = inst();
      if (fc && fc.state && fc.state.currentSection && String(fc.state.currentSection) !== String(n)) return;
      if (el.getAttribute("data-cf-unbuildable") === String(n)) return;
      el.setAttribute("data-cf-unbuildable", String(n));
      el.innerHTML =
        '<div style="padding:20px;text-align:center;color:#64748b;font-size:0.9rem;line-height:1.5;">' +
        'We could not build this section at that length.<br>' +
        'Try a longer run, or remove the gate or raked panel.' +
        '</div>';
    } catch (e) {}
  }

  function clearMark() {
    try {
      var el = document.getElementById("section-preview");
      if (el) el.removeAttribute("data-cf-unbuildable");
    } catch (e) {}
  }

  // Strip AFTER the quote has been rebuilt, never before.
  //
  // First build of this fix stripped the lines and then called updateQuoteDisplay() to
  // re-render — which REBUILDS state.quoteBreakdown from scratch and put every line
  // straight back. Measured: flat top forced to 800mm with gate+rake cleared the drawing
  // but still listed 3000mm panel, 2400mm panel, 1800 rake, gate, hinges and latch, all
  // tagged section 1. Only the posts had gone, and only because the solver produced none.
  //
  // So the strip now runs at the END of updateQuoteDisplay (the rebuild), and re-renders
  // with updateQuoteBreakdown, which only paints state and does not regenerate it.
  function apply(fc, n) {
    try {
      if (!fc || !fc.state) return;
      if (!unbuildable(fc, n)) { clearMark(); return; }
      if (stripSectionLines(fc, n)) {
        try { fc.updateQuoteBreakdown && fc.updateQuoteBreakdown(); } catch (e) {}
      }
      blankPreview(n);
    } catch (e) {
      try { console.warn("[cf-clear-unbuildable] failed:", e && e.message); } catch (e2) {}
    }
  }

  // Every section that currently has no layout — the rebuild repopulates all of them.
  function applyAll(fc) {
    try {
      var secs = fc.state && fc.state.sections;
      if (!secs) return;
      Object.keys(secs).forEach(function (k) {
        var n = parseInt(k, 10);
        if (!isNaN(n)) apply(fc, n);
      });
    } catch (e) {}
  }

  function install() {
    var fc = inst();
    if (!fc || !fc.state) return false;
    var P = Object.getPrototypeOf(fc);
    if (!P || P.__cfClearUnbuildableInstalled) return true;

    // The quote rebuild is the thing that repopulates the breakdown, so the strip has to
    // be the last word after it.
    if (typeof P.updateQuoteDisplay === "function") {
      var oQuote = P.updateQuoteDisplay;
      P.updateQuoteDisplay = function () {
        var r = oQuote.apply(this, arguments);
        try { applyAll(this); } catch (e) {}
        return r;
      };
    }

    // Also after a recalculation, which is what blanks the drawing.
    if (typeof P.updateCalculationResults === "function") {
      var orig = P.updateCalculationResults;
      P.updateCalculationResults = function (n) {
        var r = orig.apply(this, arguments);
        var self = this;
        setTimeout(function () { apply(self, n); }, 40);
        return r;
      };
    }

    P.__cfClearUnbuildableInstalled = true;
    try { console.log("[cf-clear-when-unbuildable] installed on " + location.pathname); } catch (e) {}
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) {}
    if (!done && ++TRIES < MAX_TRIES) setTimeout(tryInstall, 100);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tryInstall);
  } else {
    tryInstall();
  }
})();
